import { DestroyRef, inject, Injectable } from '@angular/core';
import { CampaignAction, CampaignsService } from '../../services/campaigns.service';
import { AlarisEditPanelService, RowActionSimple } from '@campaign-portal/components-library';
import { Campaign, CampaignStatus } from '@campaign-portal/namespace/entities/campaigns/specs';
import { exist } from '@campaign-portal/namespace/common/id';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CampaignWizardService } from '../../campaign-wizard/campaign-wizard.service';
import { CampaignUtilsService } from '../../services/campaign-utils.service';

@Injectable()
export class CampaignDetailsActionsService {
	readonly start: RowActionSimple<Campaign<exist>> = {
		icon: 'icon-play',
		label: 'actions.start',
		action: (campaign): void => {
			if (!campaign) {
				return;
			}
			this.campaignsService
				.update({
					...campaign,
					info: {
						...campaign.info,
						scheduled: new Date().toISOString(),
					},
				})
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => this.editPanel.close());
		},
		shown: (campaign): boolean => campaign?.info?.status !== CampaignStatus.PAUSED,
		disabled: (campaign): boolean => campaign?.info.status !== CampaignStatus.SCHEDULED,
	};

	readonly resume: RowActionSimple<Campaign<exist>> = {
		icon: 'icon-play',
		label: 'actions.resume',
		action: (campaign): void => {
			if (!campaign) {
				return;
			}
			this.campaignsService
				.action(CampaignAction.RESUME, campaign)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => this.editPanel.close());
		},
		shown: (campaign): boolean => campaign?.info?.status === CampaignStatus.PAUSED,
	};

	readonly pause: RowActionSimple<Campaign<exist>> = {
		icon: 'icon-pause-circle',
		label: 'actions.pause',
		action: (campaign): void => {
			if (!campaign) {
				return;
			}
			this.campaignsService
				.action(CampaignAction.PAUSE, campaign)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => this.editPanel.close());
		},
		disabled: (campaign): boolean => campaign?.info.status !== CampaignStatus.IN_PROGRESS,
	};

	readonly cancel: RowActionSimple<Campaign<exist>> = {
		icon: 'deprecated/stop',
		label: 'actions.cancel',
		action: (campaign): void => {
			if (!campaign) {
				return;
			}
			this.campaignsService
				.action(CampaignAction.CANCEL, campaign)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => this.editPanel.close());
		},
		disabled: (campaign): boolean =>
			campaign?.info.status === CampaignStatus.IN_PROGRESS ||
			campaign?.info.status === CampaignStatus.COMPLETED ||
			campaign?.info.status === CampaignStatus.FAILED ||
			campaign?.info.status === CampaignStatus.CANCELLED,
	};

	readonly repeat: RowActionSimple<Campaign<exist>> = {
		icon: 'icon-arrow-move-list',
		dynamicLabel: (campaign): string => {
			const channels = campaign?.setup.channels.map((settings) => settings.trafficType);
			const isCampaignPreModerated = (channels ?? []).some((channel) =>
				this.campaignUtils.isPreModeratedChannel(channel)
			);
			return isCampaignPreModerated ? 'actions.sendRequest' : 'actions.repeat';
		},
		action: (campaign): void => {
			if (!campaign) {
				return;
			}
			this.editPanel.close();
			this.cw.repeat(campaign);
		},
		disabled: (campaign): boolean =>
			campaign?.info.status === CampaignStatus.PAUSED ||
			campaign?.info.status === CampaignStatus.IN_PROGRESS ||
			campaign?.info.status === CampaignStatus.SCHEDULED ||
			campaign?.info.status === CampaignStatus.WAITING_FOR_APPROVAL ||
			Object.hasOwnProperty.call(campaign?.setup, 'file'),
	};

	readonly delete: RowActionSimple<Campaign<exist>> = {
		icon: '',
		label: 'actions.delete',
		action: (campaign): void => {
			if (!campaign) {
				return;
			}
			this.campaignsService
				.openDialog('Delete', campaign)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => this.editPanel.close());
		},
		disabled: (campaign): boolean => campaign?.info.status === CampaignStatus.IN_PROGRESS,
		highlight: true,
		separator: true,
	};

	private readonly destroyRef = inject(DestroyRef);

	constructor(
		private readonly campaignsService: CampaignsService,
		private readonly editPanel: AlarisEditPanelService,
		private readonly cw: CampaignWizardService,
		private readonly campaignUtils: CampaignUtilsService
	) {}
}
